import { createContext, useContext, useEffect, useState } from "react";
import en from "./en";
import sv from "./sv";
import pl from "./pl";
import pt from "./pt";
import es from "./es";
import nl from "./nl";
import fr from "./fr";
import de from "./de";
import da from "./da";
import no from "./no";
import fi from "./fi";
import patches from "./patches";
import patches2026 from "./patches2026";
import seo2026 from "./seo2026";
import structure2026 from "./structure2026";

const API = process.env.REACT_APP_BACKEND_URL;

const merge = (base, over) => {
  if (Array.isArray(base) || typeof base !== "object" || base === null) return over ?? base;
  const out = { ...base };
  Object.keys(over || {}).forEach((k) => { out[k] = merge(base[k], over[k]); });
  return out;
};

const build = (over, ...layers) => layers.reduce((acc, layer) => merge(acc, layer || {}), merge(en, over));

export const LOCALES = {
  en,
  sv: build(sv, patches.sv, patches2026.sv, seo2026.sv, structure2026.sv), pl: build(pl, patches.pl, patches2026.pl, seo2026.pl, structure2026.pl),
  pt: build(pt, patches.pt, patches2026.pt, seo2026.pt, structure2026.pt), es: build(es, patches.es, patches2026.es, seo2026.es, structure2026.es),
  nl: build(nl, patches.nl, patches2026.nl, seo2026.nl, structure2026.nl), fr: build(fr, patches.fr, patches2026.fr, seo2026.fr, structure2026.fr),
  de: build(de, patches2026.de, seo2026.de, structure2026.de), da: build(da, patches2026.da, seo2026.da, structure2026.da),
  no: build(no, patches2026.no, seo2026.no, structure2026.no), fi: build(fi, patches2026.fi, seo2026.fi, structure2026.fi),
};

export const LANGS = [
  { code: "en", label: "EN", name: "English" },
  { code: "sv", label: "SV", name: "Svenska" },
  { code: "pt", label: "PT", name: "Português" },
  { code: "pl", label: "PL", name: "Polski" },
  { code: "fr", label: "FR", name: "Français" },
  { code: "es", label: "ES", name: "Español" },
  { code: "de", label: "DE", name: "Deutsch" },
  { code: "da", label: "DA", name: "Dansk" },
  { code: "no", label: "NO", name: "Norsk" },
  { code: "fi", label: "FI", name: "Suomi" },
  { code: "nl", label: "NL", name: "Nederlands" },
];

// Map country (world-map lowercase codes) -> preferred initial language.
export const MAP_CODE_TO_LANG = { se: "sv", pt: "pt", pl: "pl", be: "fr", es: "es", de: "de", dk: "da", no: "no", fi: "fi" };

const getSaved = () => {
  const s = localStorage.getItem("nv_lang");
  return s && LOCALES[s] ? s : null;
};
const isManual = () => localStorage.getItem("nv_lang_manual") === "1";
const fromQuery = () => {
  const q = new URLSearchParams(window.location.search).get("lang");
  return q && LOCALES[q] ? q : null;
};
const browserLang = () => {
  const b = (navigator.language || "en").slice(0, 2).toLowerCase();
  return LOCALES[b] ? b : "en";
};

const LangContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => fromQuery() || getSaved() || browserLang());

  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  // Country/IP based INITIAL language — only if the user hasn't manually chosen one.
  useEffect(() => {
    if (isManual()) return undefined;
    if (fromQuery()) return undefined;
    let cancelled = false;
    fetch(`${API}/api/i18n/preferred-language`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled || isManual()) return;
        if (d && d.lang && LOCALES[d.lang]) {
          localStorage.setItem("nv_lang", d.lang); // remembered as detected (not manual)
          setLangState(d.lang);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // manual=true (default) when the visitor picks from the selector; manual=false for
  // automatic switches (e.g. tapping a country on the map) which must never override
  // an explicit manual choice.
  const setLang = (code, { manual = true } = {}) => {
    if (!LOCALES[code]) return;
    if (!manual && isManual()) return;
    localStorage.setItem("nv_lang", code);
    if (manual) localStorage.setItem("nv_lang_manual", "1");
    setLangState(code);
  };

  return <LangContext.Provider value={{ lang, setLang, t: LOCALES[lang] }}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);

export const fill = (str, vars) => str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`));
